import { isTruthy } from '@chainflip/utils/guard';
import { getDefaultConfig } from '@rainbow-me/rainbowkit';
import {
  bitgetWallet,
  coinbaseWallet,
  metaMaskWallet,
  okxWallet,
  rabbyWallet,
  rainbowWallet,
  subWallet,
  talismanWallet,
  walletConnectWallet,
  xdefiWallet,
  phantomWallet,
  trustWallet,
} from '@rainbow-me/rainbowkit/wallets';
import { fallback, http, type Chain as WagmiChain } from 'viem';
import { arbitrum, arbitrumSepolia, bsc, bscTestnet, mainnet, sepolia } from 'wagmi/chains';
import { chains, backspinArbitrum, backspinEthereum } from '@/shared/assets/chains';
import { isTestnet } from '@/shared/featureFlags';
import { getEthereumChain } from '@/shared/utils/env';

const projectId = '9d94e593343f9901d073e88eb0c28c18'; // managed by 0xa56A6be23b6Cf39D9448FF6e897C29c41c8fbDFF

const customTransports: Record<number, ReturnType<typeof fallback>> = {
  [mainnet.id]: fallback([
    http(`https://mainnet.infura.io/v3/${process.env.NEXT_PUBLIC_INFURA_API_KEY}`),
    http(),
  ]),
  [sepolia.id]: fallback([http('https://ethereum-sepolia-rpc.publicnode.com'), http()]),
  [arbitrum.id]: fallback([
    http(`https://arbitrum-mainnet.infura.io/v3/${process.env.NEXT_PUBLIC_INFURA_API_KEY}`),
    http(),
  ]),
  [arbitrumSepolia.id]: fallback([http('https://arbitrum-sepolia-rpc.publicnode.com'), http()]),
  [bsc.id]: fallback([http('https://bsc-rpc.publicnode.com'), http()]),
  [bscTestnet.id]: fallback([http('https://bsc-testnet-rpc.publicnode.com'), http()]),
  [backspinEthereum.wagmiChain.id]: fallback([
    http(backspinEthereum.wagmiChain.rpcUrls.default.http as unknown as string),
  ]),
  [backspinArbitrum.wagmiChain.id]: fallback([
    http(backspinArbitrum.wagmiChain.rpcUrls.default.http as unknown as string),
  ]),
  // TODO: check if tron works with metamask on mainnet
  // [tronNile.id]: fallback([
  //   http(tronTestnet.wagmiChain.rpcUrls.default.http as unknown as string),
  //   http(),
  // ]),
  // [tron.id]: fallback([http('https://api.trongrid.io/jsonrpc'), http()]),
};

const getConfig = (wagmiChains: [WagmiChain, ...WagmiChain[]]) =>
  getDefaultConfig({
    appName: 'Chainflip App',
    projectId,
    chains: wagmiChains,
    ssr: true,
    transports: Object.fromEntries(
      wagmiChains.map((chain) => [chain.id, customTransports[chain.id] ?? http()]),
    ),
    wallets: [
      {
        groupName: 'Recommended',
        wallets: [
          metaMaskWallet,
          rabbyWallet,
          phantomWallet,
          trustWallet,
          rainbowWallet,
          walletConnectWallet,
          !isTestnet ? xdefiWallet : undefined,
          coinbaseWallet,
          talismanWallet,
          bitgetWallet,
          okxWallet,
          subWallet,
        ].filter(isTruthy),
      },
    ],
  });

const withoutWallets =
  (walletNames: string[]) =>
  <T extends ReturnType<typeof getConfig>>(config: T): T => {
    const { _internal: internal } = config;
    internal.connectors.subscribe((connectors) => {
      const filtered = connectors.filter((c) => !walletNames.some((name) => c.name === name));
      if (filtered.length !== connectors.length) {
        internal.connectors.setState(filtered);
      }
    });
    return config;
  };

export const defaultRainbowConfig = withoutWallets(['TronLink'])(
  getConfig([getEthereumChain().wagmiChain]),
);

export const multiChainRainbowConfig = withoutWallets(['TronLink'])(
  getConfig(chains.map((c) => c.wagmiChain).filter(isTruthy) as [WagmiChain, ...WagmiChain[]]),
);
